import { Component, computed, HostListener, inject, Input, OnChanges, OnDestroy, OnInit, signal, SimpleChanges } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { combineLatest, interval, Observable, of, startWith, Subject, switchMap, tap } from "rxjs";
import { MarketService } from "../../../../../../../shared/src/lib/services/market.service";
import { AsyncPipe } from "@angular/common";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { HRowComponent } from "./h-row/h-row.component";
import { LiveUpdateService } from "../../../../../../../shared/src/lib/services/live-update.service";
import { StatusAlertComponent } from "../../../../components/status-alert/status-alert.component";
import { genericSort } from "../../../../../../../shared/src/lib/utils/market";
import { CoreService } from "../../../../../../../shared/src/lib/services/core.service";
import { BetPlacementService } from "../../../../../../../shared/src/lib/services/bet-placement.service";
import { TranslateModule } from "@ngx-translate/core";
import { LoaderService } from "../../../../../../../shared/src/lib/services/loader.service";
import { HighlightLoaderComponent } from "./highlight-loader/highlight-loader.component";
import { environment } from "../../../../../../../shared/src/environments/environment";
import { MarketOpsService } from "../../market-layout/market/market-ops.service";
import { catchError, filter, map, take, takeUntil } from "rxjs/operators";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { DisplayModeService } from "../../../../../../../shared/src/lib/services/display-mode.service";

@Component({
  selector: "app-sport-highlights",
  imports: [MatProgressBarModule, HRowComponent, StatusAlertComponent, TranslateModule, HighlightLoaderComponent, AsyncPipe, MatIcon, MatTooltip],
  templateUrl: "./sport-highlights.component.html",
  styleUrl: "./sport-highlights.component.scss",
})
export class SportHighlightsComponent implements OnInit, OnDestroy, OnChanges {
  destroy$ = new Subject<void>();
  sportIds: Record<string, string> = {
    soccer: "1",
    cricket: "4",
    tennis: "2",
    horse: "7",
    greyhound: "4339",
    basketball: "7552",
  };
  tHeaders: string[] = [];
  pageTitle = "";
  titles: Record<string, string> = {
    soccer: "Soccer",
    cricket: "Cricket",
    tennis: "Tennis",
    horse: "Horse",
    greyhound: "Greyhound",
    basketball: "Basketball",
  };
  markets = signal<any[]>([]);
  marketItems: string[] = [];
  currentSport: any = "";
  marketService = inject(MarketService);
  liveUpdateService = inject(LiveUpdateService);
  coreService = inject(CoreService);
  placementService = inject(BetPlacementService);
  $loading!: Observable<boolean>;
  loader = inject(LoaderService);
  inPlayMarkets: string[] = [];
  marketOps = inject(MarketOpsService);
  parents: any[] = []; // max no of parents
  betPlacementService = inject(BetPlacementService);
  currentSort = signal<{ field: string; order: "asc" | "desc" } | null>(null);
  isLoadingStatus = signal(false);
  router = inject(Router);
  route = inject(ActivatedRoute);
  marketBackup: any[] = [];
  readonly marketsLength = computed(() => this.markets().length);
  @Input() type: "highlights" | "popular" = "highlights";
  @Input() data: { key: string; value: any[] } | null = null;
  @Input() showHeader = true;
  isCompetitionView = signal(false);
  displayModeService = inject(DisplayModeService);
  collapsedLeagues = signal<Set<string>>(new Set());
  openDropdown = signal<"event" | "volume" | null>(null);

  groupedMarkets = computed(() => {
    const markets = this.markets();
    const mode = this.displayModeService.mode();
    if (mode === "TIME-WISE" || this.isCompetitionView()) {
      return null;
    }

    // Group by league
    const groupsMap = new Map<string, { id: string; name: string; markets: any[] }>();
    for (const market of markets) {
      const leagueId = market.competition?.id || "other";
      const leagueName = market.competition?.name || "Other";
      if (!groupsMap.has(leagueId)) {
        groupsMap.set(leagueId, { id: leagueId, name: leagueName, markets: [] });
      }
      groupsMap.get(leagueId)?.markets.push(market);
    }
    return Array.from(groupsMap.values());
  });

  ngOnInit() {
    this.isLoadingStatus.set(true);
    if (this.data) {
      this.handleExternalData();
      return;
    }

    this.$loading = combineLatest([this.loader.isLoading("/bf/match-odds-markets"), this.loader.isLoading("/v1/api/navigation_list")]).pipe(
      map(([loadingMarkets, loadingNavigationList]) => loadingMarkets || loadingNavigationList),
    );

    const params$ = this.route.parent
      ? combineLatest([this.route.params, this.route.parent.params]).pipe(map(([p1, p2]) => ({ ...p1, ...p2 })))
      : this.route.params;

    combineLatest([params$, this.route.data, this.route.queryParams])
      .pipe(
        switchMap(([params, data, qParams]) => {
          if (data["type"]) this.type = data["type"];
          const sport = params["sport"];
          const leagueId = params["leagueId"];
          const leagueName = params["leagueName"];
          this.isCompetitionView.set(!!leagueId);

          if (!sport) return of(null);

          this.pageTitle = leagueName || "...";
          this.currentSport = sport;

          return interval(120000).pipe(
            startWith(0),
            switchMap(() => {
              if (leagueId) {
                return this.getLeagueMarkets(leagueId);
              }
              if (this.type === "popular") {
                return this.getPopularMarkets(sport);
              }
              return this.getMatchOdds(sport);
            }),
          );
        }),
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["data"] && this.data) {
      this.handleExternalData();
    }
  }

  getLeagueMarkets(leagueId: string): Observable<any> {
    this.isLoadingStatus.set(true);
    return this.marketService.fetchCompetitionMarkets(leagueId).pipe(
      tap({
        next: (response: any) => {
          if (response?.data && Array.isArray(response?.data)) {
            this.handlePromotedMarkets(response.data);
          } else {
            this.setFallback();
          }
        },
        error: (error: any) => {
          console.error(error);
          this.setFallback();
        },
      }),
    );
  }

  getPopularMarkets(sport: string): Observable<any> {
    this.isLoadingStatus.set(true);
    const sportId = this.sportIds[sport];

    return this.marketService.promoted$.pipe(
      filter((data) => !!data),
      take(1),
      switchMap((promotedData) => {
        if (["1", "4"].includes(sportId)) {
          // Soccer or Cricket: Collect markets from promotedLeagues
          const leagues = promotedData.promotedLeagues || [];
          const sportLeagues = leagues.filter((l: any) => String(l.sportId) === sportId);
          const seen = new Set<string>();
          const allMarkets = sportLeagues
            .reduce((acc: any[], league: any) => [...acc, ...(league.markets || [])], [])
            .filter((m: any) => { const k = m.marketId; return seen.has(k) ? false : !!seen.add(k); });

          if (allMarkets.length > 0) {
            this.handlePromotedMarkets(allMarkets);
            return of(allMarkets);
          }
        } else if (sportId === "2") {
          // Tennis: Fetch from promotedEvents
          const events = promotedData.promotedEvents || [];
          const tennisEvents = events.filter((e: any) => String(e.sportId) === "2");

          if (tennisEvents.length > 0) {
            const requests = tennisEvents.map((event: any) =>
              this.marketService.getEventMarkets({ id: event.eventId }).pipe(
                map((res: any) => res?.data?.markets || []),
                catchError(() => of([])),
              ),
            );

            return combineLatest(requests).pipe(
              tap((results: any) => {
                const allMarkets = results.flat();
                if (allMarkets.length > 0) {
                  this.handlePromotedMarkets(allMarkets);
                } else {
                  this.setFallback();
                }
              }),
            );
          }
        }

        // Fallback for other sports or if no promoted data found
        return this.getMatchOdds(sport);
      }),
    );
  }

  private handlePromotedMarkets(allMarkets: any[]) {
    this.isLoadingStatus.set(true);
    this.inPlayMarkets = [];
    this.marketItems = allMarkets.map((market: any) => `bf_${market?.marketId}`);
    const params = { market_ids: this.marketItems.join(",") };
    this.getMarketStatus(params, allMarkets);
    this.marketOps.setTopics(this.marketItems);
  }

  getMatchOdds(sport: string): Observable<any> {
    this.isLoadingStatus.set(true);
    const params = {
      eventTypeId: this.sportIds[sport],
      maxResults: environment?.maxHighlightResults,
    };

    return this.marketService.fetchMatchOdds(params).pipe(
      tap({
        next: (response: any) => {
          if (response?.data && Array.isArray(response?.data)) {
            this.inPlayMarkets = [];
            this.marketItems = response?.data?.map((market: any) => `bf_${market?.marketId}`);
            const params = { market_ids: this.marketItems?.join(",") };
            this.getMarketStatus(params, response?.data);
            this.marketOps.setTopics(this.marketItems);
          } else {
            this.setFallback();
          }
        },
        error: (error: any) => {
          console.error(error);
          this.setFallback();
        },
      }),
    );
  }

  getMarketStatus(params: any, marketItems: any[]) {
    const qParams = { ...params, statuses: "2,3", return_snapshot: "Y" };

    this.marketService
      .fetchMarketStatus(qParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const { snapshot: snapshots = {}, statuses = {} } = response || {};
          if (marketItems && Array.isArray(marketItems)) {
            const openedMarkets = statuses["2"] || [];
            const suspendedMarkets = statuses["3"] || [];
            let _markets = marketItems
              .filter((market: any) => {
                const _marketId = `bf_${market?.marketId}`;
                return openedMarkets.includes(_marketId) || suspendedMarkets.includes(_marketId);
              })
              .map((market: any) => {
                const _marketId = `bf_${market?.marketId}`;
                return {
                  ...market,
                  mc: snapshots[_marketId],
                  eventName: market.event.name,
                };
              });
            this.betPlacementService.currentPlacementConcernsMe(_markets, "marketId");
            this.setMarkets(_markets);
          } else {
            this.setFallback();
          }
        },
        error: (error) => {
          console.error("Failed to fetch market statuses:", error);
        },
      });
  }

  setMarkets(marketData: any[]) {
    if (!marketData) return;

    const hasDraw = marketData.some((item) => item?.runners?.length > 2);

    this.tHeaders = hasDraw ? ["1", "Draw", "2"] : ["1", "2"];

    const markets = marketData.map((item) => {
      let runners = [...(item?.runners || [])];

      if (hasDraw) {
        if (runners.length === 2) {
          runners = [...runners, { runnerName: "The Draw", sortPriority: 3 }];
        }
        // Sort by sortPriority
        runners.sort((a, b) => (a?.sortPriority || 0) - (b?.sortPriority || 0));
        // Pick first 3
        runners = runners.slice(0, 3);
        // Reorder: [1st, 3rd, 2nd] to match [1, Draw, 2] headers
        const [r1, r2, r3] = runners;
        runners = [r1, r3, r2];
      }

      return { ...item, runners };
    });

    const sort = this.currentSort();
    this.handleSort(markets, sort ? [sort.field] : ["marketStartTime", "eventName"], sort?.order);
    this.marketBackup = markets;
  }

  onSort(field: string, order: "asc" | "desc" | null) {
    if (order === null) {
      this.currentSort.set(null);
    } else {
      this.currentSort.set({ field, order });
    }

    const sort = this.currentSort();
    const sortFields = sort ? [sort.field] : ["marketStartTime", "eventName"];

    this.handleSort(this.markets(), sortFields, sort?.order);
  }

  handleSort(markets: any[], sortFields: string[], sortOrder?: "asc" | "desc") {
    const _markets = genericSort([...markets], sortFields, sortOrder);
    this.markets.set(_markets);

    const params = { ...this.route.snapshot.params, ...this.route.parent?.snapshot.params };
    const qParams = { ...this.route.snapshot.queryParams, ...this.route.parent?.snapshot.queryParams };

    if (params["leagueId"]) {
      this.pageTitle = params["leagueName"] || (_markets.length > 0 ? _markets[0].competition?.name : this.pageTitle);
    } else if (this.data) {
      this.pageTitle = this.titles[this.currentSport] || this.pageTitle;
    } else {
      const titleSuffix = this.type === "popular" ? "Popular" : "Highlights";
      this.pageTitle = `${this.titles[this.currentSport]} ${titleSuffix}`;
    }

    this.isLoadingStatus.set(false);
  }

  toggleLeagueCollapse(leagueId: string) {
    this.collapsedLeagues.update((prev) => {
      const next = new Set(prev);
      if (next.has(leagueId)) {
        next.delete(leagueId);
      } else {
        next.add(leagueId);
      }
      return next;
    });
  }

  isLeagueCollapsed(leagueId: string): boolean {
    return this.collapsedLeagues().has(leagueId);
  }

  toggleDropdown(type: "event" | "volume", event: Event) {
    event.stopPropagation();
    this.openDropdown.update((current) => (current === type ? null : type));
  }

  @HostListener("window:click")
  closeDropdown() {
    this.openDropdown.set(null);
  }

  private handleExternalData() {
    this.isLoadingStatus.set(true);
    if (!this.data) return;
    const separator = this.data.key.indexOf("_") + 1;
    this.pageTitle = this.data.key.substring(separator);
    this.currentSport = this.pageTitle.toLowerCase();
    this.handlePromotedMarkets(this.data.value);
  }

  isSortActive(field: string, order: "asc" | "desc" | null): boolean {
    const sort = this.currentSort();
    if (order === null) return sort === null;
    return sort?.field === field && sort?.order === order;
  }

  setFallback() {
    this.isLoadingStatus.set(false);
    this.markets.set([]);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.marketOps.setTopics([]);
  }
}
